import { inject, Injectable, signal } from '@angular/core';
import { LoggerService } from './logger.service';

interface ClockSyncPongMessage {
  type: 'clock_pong';
  client_send_time: number;
  server_time: number;
}

/**
 * Clock synchronization service using NTP-style protocol
 * Ensures consistent timestamps across clients for conflict resolution
 */
@Injectable({
  providedIn: 'root',
})
export class ClockSyncService {
  private logger = inject(LoggerService).getLogger('clock-sync');
  private offset = signal(0);
  private lastSync = signal(0);
  private pendingPings = new Map<number, (pong: ClockSyncPongMessage) => void>();

  /**
   * Get server-corrected current timestamp
   * Use this instead of Date.now() for all sync-related timestamps
   */
  now(): number {
    return Date.now() + this.offset();
  }

  /**
   * Check if clock resync is needed (every 5 minutes)
   */
  needsResync(): boolean {
    const timeSinceLastSync = Date.now() - this.lastSync();
    return timeSinceLastSync > 300_000; // 5 minutes
  }

  /**
   * Perform clock synchronization with server
   * Calculates offset using round-trip time estimation
   */
  async sync(ws: WebSocket): Promise<void> {
    const clientSendTime = Date.now();

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingPings.delete(clientSendTime);
        reject(new Error('Clock sync timeout'));
      }, 5000);

      this.pendingPings.set(clientSendTime, (pong) => {
        clearTimeout(timeoutId);
        const clientReceiveTime = Date.now();

        // Calculate round-trip time and latency
        const rtt = clientReceiveTime - clientSendTime;
        const latency = rtt / 2;

        // Calculate offset: server_time - (client_receive_time - latency)
        const newOffset = pong.server_time - (clientReceiveTime - latency);

        this.offset.set(newOffset);
        this.lastSync.set(Date.now());

        this.logger.debug(
          `Synchronized: offset=${newOffset}ms, latency=${latency}ms, rtt=${rtt}ms`,
        );

        resolve();
      });

      ws.send(
        JSON.stringify({
          type: 'clock_ping',
          client_send_time: clientSendTime,
        }),
      );
    });
  }

  /**
   * Handle clock_pong message from server
   */
  handlePong(pong: ClockSyncPongMessage): void {
    const resolver = this.pendingPings.get(pong.client_send_time);
    if (resolver) {
      resolver(pong);
      this.pendingPings.delete(pong.client_send_time);
    }
  }

  /**
   * Get current offset for debugging
   */
  getOffset(): number {
    return this.offset();
  }

  /**
   * Get last sync timestamp for debugging
   */
  getLastSyncTime(): number {
    return this.lastSync();
  }
}
